import React, { Fragment, useContext } from 'react'
import { Separator, InputRow } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { usePermission } from '@fs/zion-permissions'
import CardRow from './card/CardRow'
import Card from './card/Card'
import SectionHeader from './SectionHeader'
import useIsMobile from '../hooks/useIsMobile'
import { markItemsInPlaylist } from '../global/playlistHelper'
import { PlaylistContext } from './PlaylistContext'
import { getLanguageFromLocale } from '../helpers/languageNameInLanguage'
import BS_OBJECT_TYPES from '../api/brightspot/objectTypeIds'
import getCreatorsNames from '../helpers/creatorsNames'
import ChildLimiter from './ChildLimiter'
import TopicTag from './search/TopicTag'
import getIsLive from '../helpers/isLiveHelper'

const ContentCardRow = ({
  items,
  headingText,
  isPlaylistable,
  largeMobileImg = false,
  smallHeader = false,
  headerUppercase = false,
  imageFit,
  extraContent,
  hideCardTitles,
  centered,
  useChildLimiter = false,
  tags,
  showMoreAmount,
  initialCount = 4,
  openInNewTab,
}) => {
  const user = useUser()
  const isMobile = useIsMobile()
  const { playlist } = useContext(PlaylistContext)
  const markedItems = markItemsInPlaylist({ playlist, items })
  const isMember = usePermission('MemberContentPermission', 'DiscoveryUserContext')
  // Because of how I set up the ChildLimiter component to work in multiple situations,
  // we have to declare the CardRow with the centered prop in multiple spots. Doing it
  // this way makes it so if any other changes are needed in the future, it's easy to
  // just add them here in one spot, rather than having CardRow in multiple places
  const CardRowHelper = ({ children }) => <CardRow centered={centered}>{children}</CardRow>
  const CardWrapper = ({ children }) => {
    if (useChildLimiter) {
      return (
        <ChildLimiter initialCount={initialCount} showMoreAmount={showMoreAmount} childWrapper={CardRowHelper}>
          {children}
        </ChildLimiter>
      )
    }
    return <CardRowHelper>{children}</CardRowHelper>
  }

  return (
    <>
      <SectionHeader heading={headingText} headerUppercase={headerUppercase} small={smallHeader || isMobile} />
      {tags?.length > 0 && (
        <InputRow>
          {tags.map((tag) => (
            <TopicTag tag={tag} key={tag} />
          ))}
        </InputRow>
      )}
      <Separator />
      <CardWrapper>
        {markedItems?.map((contentItem) => (
          <Fragment key={contentItem?.contentUUID || Math.random()}>
            {(!contentItem?.membersOnly || (contentItem?.membersOnly && isMember)) && (
              <Card
                data-testid={`card-${contentItem.contentUUID}`}
                isInPlaylist={contentItem.isInPlaylist}
                cisId={user?.cisId}
                isPlaylistable={isPlaylistable && user?.cisId}
                key={contentItem.contentUUID}
                url={contentItem.url}
                openInNewTab={openInNewTab}
                image={
                  contentItem?.image?.custom?.image?.src ??
                  contentItem?.creators?.[0]?.profilePhoto?.custom?.image?.src ??
                  contentItem?.profilePhoto?.custom?.image?.src
                }
                spokenLanguage={getLanguageFromLocale({
                  locale: contentItem?.locale,
                  parentLocale: contentItem?.parentLocale,
                })}
                isSeries={contentItem?.objectTypeId === BS_OBJECT_TYPES.SERIES}
                isLive={getIsLive(contentItem)}
                primaryText={!hideCardTitles ? contentItem?.title ?? contentItem?.headingText : ''}
                secondaryText={
                  getCreatorsNames(contentItem?.creators) ?? contentItem.description ?? contentItem.detailText
                }
                contentUUID={contentItem.contentUUID}
                sessionLength={contentItem?.objectTypeId === BS_OBJECT_TYPES.SESSION ? null : null}
                largeMobileImg={largeMobileImg}
                imageFit={imageFit}
                goLiveTime={contentItem?.date?.timestamp}
                extraContent={extraContent}
              />
            )}
          </Fragment>
        ))}
      </CardWrapper>
    </>
  )
}

export default ContentCardRow
